import React, { useEffect, useState } from "react";
import { alertError } from "../../utils/logger";
import Table, { buttonsStyle, dateFormatter } from "../components/tables/table";
import {
  deleteInvoice,
  downloadInvoicePdf,
  getAllInvoices,
  refundFullInvoice,
  regenerateInvoice,
} from "../../api/invoice";
import { Button, TextField, Tooltip } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import DeleteIcon from "@material-ui/icons/Delete";
import GetAppIcon from "@material-ui/icons/GetApp";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import ReplayIcon from "@material-ui/icons/Replay";
import { Col, Row } from "react-bootstrap";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../_metronic/_partials/controls";
import FiltersCard from "../components/filters/Filter";
import { getAllCustomers } from "../../api/customer";

function eurosFromCents(cents) {
  const n = Number(cents ?? 0) / 100;
  return n.toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function SubscriptionPaymentsPage() {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [tableKey, setTableKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isBlocking, setIsBlocking] = useState(false);

  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [dateField, setDateField] = useState("issuedAt"); // "periodStart" | "issuedAt"
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  function hasPdf(invoice) {
    if (!invoice) return false;
    if (invoice?.status === "pdf_generated") return true;
    if (
      invoice?.status !== "issued" &&
      invoice?.pdf?.path &&
      String(invoice.pdf.path).trim() !== ""
    )
      return true;
    return false;
  }

  function applyFilters(list) {
    let out = Array.isArray(list) ? list : [];

    if (selectedCustomers?.length) {
      const allowed = new Set(
        selectedCustomers
          .map((c) => c?._id || c?.id)
          .filter(Boolean)
          .map(String),
      );
      out = out.filter((inv) =>
        allowed.has(String(inv?.customer?._id || inv?.customer)),
      );
    }

    const parseDate = (s) => {
      if (!s) return null;
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    const from = parseDate(dateFrom);
    const to = parseDate(dateTo);
    if (from || to) {
      // Include full 'to' day
      const toEnd = to
        ? new Date(
            to.getFullYear(),
            to.getMonth(),
            to.getDate(),
            23,
            59,
            59,
            999,
          )
        : null;
      out = out.filter((inv) => {
        const raw = inv?.[dateField];
        if (!raw) return false;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return false;
        if (from && d < from) return false;
        if (toEnd && d > toEnd) return false;
        return true;
      });
    }

    return out;
  }

  function handleSearchFilters() {
    setRows(applyFilters(allRows));
    setTableKey((k) => k + 1);
  }

  function handleClearFilters() {
    setSelectedCustomers([]);
    setDateField("issuedAt");
    setDateFrom("");
    setDateTo("");
    setRows(allRows);
    setTableKey((k) => k + 1);
  }

  async function handleDownload(row) {
    const id = row?._id;
    if (!id) return;
    try {
      const res = await downloadInvoicePdf(id);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `factura-${row?.numInvoice || id}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (error) {
      alertError({ error, customMessage: "Could not download invoice PDF." });
    }
  }

  async function handleRegenerate(row) {
    const id = row?._id;
    if (!id) return;
    setIsBlocking(true);
    try {
      await regenerateInvoice(id);
      // refresh list
      const invoicesRes = await getAllInvoices({ limit: 500, skip: 0 });
      const pdfRows = (invoicesRes.data || []).filter(hasPdf);
      setAllRows(pdfRows);
      setRows(applyFilters(pdfRows));
      setTableKey((k) => k + 1);
    } catch (error) {
      alertError({ error, customMessage: "Could not regenerate invoice PDF." });
    } finally {
      setIsBlocking(false);
    }
  }

  async function handleRefundFull(row) {
    const id = row?._id;
    if (!id) return;
    const ok = window.confirm(
      `Create full refund invoice for ${row?.numInvoice ||
        id}? This will generate a PDF and mark the invoice as refunded.`,
    );
    if (!ok) return;

    setIsBlocking(true);
    try {
      await refundFullInvoice(id);
      const invoicesRes = await getAllInvoices({ limit: 500, skip: 0 });
      const pdfRows = (invoicesRes.data || []).filter(hasPdf);
      setAllRows(pdfRows);
      setRows(applyFilters(pdfRows));
      setTableKey((k) => k + 1);
    } catch (error) {
      alertError({ error, customMessage: "Could not create refund invoice." });
    } finally {
      setIsBlocking(false);
    }
  }

  async function handleDelete(row) {
    const id = row?._id;
    if (!id) return;
    const ok = window.confirm(
      `Delete invoice ${row?.numInvoice || id}? This cannot be undone.`,
    );
    if (!ok) return;

    setIsBlocking(true);
    try {
      await deleteInvoice(id);
      const invoicesRes = await getAllInvoices({ limit: 500, skip: 0 });
      const pdfRows = (invoicesRes.data || []).filter(hasPdf);
      setAllRows(pdfRows);
      setRows(applyFilters(pdfRows));
      setTableKey((k) => k + 1);
    } catch (error) {
      alertError({ error, customMessage: "Could not delete invoice." });
    } finally {
      setIsBlocking(false);
    }
  }

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const invoicesRes = await getAllInvoices({ limit: 500, skip: 0 });
        const list = invoicesRes.data || [];
        const pdfRows = list.filter(hasPdf);
        setAllRows(pdfRows);
        setRows(applyFilters(pdfRows));
        setTableKey((k) => k + 1);
      } catch (error) {
        alertError({ error, customMessage: "Could not load invoices." });
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    // Load customers for filter selector
    getAllCustomers("seller,buyer,advisor")
      .then((res) => {
        const opts = Array.isArray(res.data) ? res.data : [];
        setCustomerOptions(opts);
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "Could not load customers for filters.",
        });
      });
  }, []);

  const columns = [
    {
      dataField: "customer.fullName",
      text: "Customer",
      sort: true,
      formatter: (_cell, row) => row?.customer?.fullName || "—",
    },
    {
      dataField: "numInvoice",
      text: "Invoice #",
      sort: true,
      formatter: (cell, row) => row?.numInvoice || cell || "—",
    },
    {
      dataField: "planTitle",
      text: "Plan",
      sort: true,
      formatter: (_cell, row) => {
        return row?.planTitle || "—";
      },
    },
    {
      dataField: "periodStart",
      text: "Start",
      sort: true,
      formatter: dateFormatter,
    },
    {
      dataField: "periodEnd",
      text: "End",
      sort: true,
      formatter: dateFormatter,
    },
    {
      dataField: "amounts.totalCents",
      text: "Total",
      sort: true,
      formatter: (_cell, row) =>
        `${eurosFromCents(row?.amounts?.totalCents)} €`,
    },
    {
      dataField: "actions",
      text: "",
      formatter: (_cell, row) => (
        <div className="d-flex align-items-center">
          <Tooltip title="Download">
            <span>
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => handleDownload(row)}
                disabled={isBlocking}
              >
                <GetAppIcon />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Regenerate PDF">
            <span>
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => handleRegenerate(row)}
                disabled={isBlocking}
              >
                <AutorenewIcon />
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              row?.kind === "refund"
                ? "This row is already a refund invoice"
                : row?.refundInvoice || row?.refundedAt
                ? "This invoice was already refunded"
                : "Create refund invoice"
            }
          >
            <span>
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => handleRefundFull(row)}
                disabled={
                  isBlocking ||
                  row?.kind === "refund" ||
                  !!row?.refundInvoice ||
                  !!row?.refundedAt ||
                  !Number(row?.amounts?.totalCents)
                }
              >
                <ReplayIcon />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <span>
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => handleDelete(row)}
                disabled={isBlocking}
              >
                <DeleteIcon />
              </Button>
            </span>
          </Tooltip>
        </div>
      ),
      headerStyle: { width: "220px" },
    },
  ];

  return (
    <>
      {isBlocking && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(255,255,255,0.35)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "all",
          }}
          aria-busy="true"
          aria-live="polite"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              padding: "18px 22px",
              background: "rgba(255,255,255,0.85)",
              borderRadius: "12px",
              boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
            }}
          >
            <div className="spinner-border text-primary" role="status" />
            <div style={{ fontWeight: 600 }}>Processing…</div>
          </div>
        </div>
      )}
      <FiltersCard
        collapsed={filtersCollapsed}
        setCollapsed={setFiltersCollapsed}
        handleClearFilters={handleClearFilters}
        handleSearch={handleSearchFilters}
        filtersContent={() => (
          <>
            <br />
            <Row>
              <Col lg={4} className="mb-4">
                <Autocomplete
                  multiple
                  options={customerOptions}
                  value={selectedCustomers}
                  getOptionLabel={(option) =>
                    option?.fullName || option?.cif || option?.email || "—"
                  }
                  onChange={(_e, newValue) =>
                    setSelectedCustomers(newValue || [])
                  }
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customers"
                      margin="normal"
                      variant="outlined"
                    />
                  )}
                />
              </Col>
              <Col lg={4} className="mb-4">
                <TextField
                  label="Desde"
                  type="date"
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Col>
              <Col lg={4} className="mb-4">
                <TextField
                  label="Hasta"
                  type="date"
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Col>
            </Row>
          </>
        )}
      />
      <Card>
        <CardHeader title="Subscription payments">
          <CardHeaderToolbar>
            <div style={{ opacity: 0.7, paddingRight: "0.5rem" }}>
              {loading ? "Loading..." : ""}
            </div>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <Table
            key={tableKey}
            search={true}
            data={rows}
            columns={columns}
            keyField="_id"
          />
        </CardBody>
      </Card>
    </>
  );
}
